import { useEffect, useMemo, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { FinishScreen } from "@/components/FinishScreen";
import { QUOTES, pick, shuffle, quotesByPack, type PackId } from "@/data/quotes";
import { ANSWER_LETTERS, ROUND_COUNT, loadBest, saveBest, loadStreak, saveStreak } from "@/lib/game-utils";

interface Round { quoteId: string; options: { text: string; real: boolean }[]; author: string; }

function buildRounds(pack: PackId): Round[] {
  const pool = quotesByPack(pack).filter((q) => q.fake);
  const source = pool.length >= ROUND_COUNT ? pool : QUOTES.filter((q) => q.fake);
  return pick(source, ROUND_COUNT).map((q) => ({
    quoteId: q.id,
    author: q.author,
    options: shuffle([{ text: q.text, real: true }, { text: q.fake!, real: false }]),
  }));
}

export function TrueOrFakeGame({ pack = "all" }: { pack?: PackId }) {
  const live = useLiveMode();
  const [rounds, setRounds] = useState<Round[]>(() => buildRounds(pack));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const [best, setBest] = useState(0);
  useEffect(() => { setBest(loadBest("true-or-fake")); setBestStreak(loadStreak("true-or-fake")); }, []);

  const round = rounds[idx];
  const correctIdx = useMemo(() => round.options.findIndex((o) => o.real), [round]);

  const answer = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === correctIdx) {
      setScore((s) => s + 1);
      setStreak((s) => { const n = s + 1; if (n > bestStreak) setBestStreak(saveStreak("true-or-fake", n)); return n; });
    } else setStreak(0);
  };
  const next = () => {
    if (idx + 1 >= rounds.length) { setBest(saveBest("true-or-fake", score)); setDone(true); }
    else { setIdx(idx + 1); setPicked(null); }
  };
  const restart = () => { setRounds(buildRounds(pack)); setIdx(0); setScore(0); setStreak(0); setPicked(null); setDone(false); };

  if (done) return <FinishScreen score={score} total={rounds.length} best={best} bestStreak={bestStreak} onRestart={restart} title="Real Quote or Fake" numeral="IV" hubSlug="true-or-fake" />;

  return (
    <GameShell title="Real Quote or Fake" numeral="IV" score={score} round={idx + 1} total={rounds.length} streak={streak} timeLimit={25} timeKey={idx} onTimeUp={() => picked === null && setPicked(-1)} hubSlug="true-or-fake">
      <div className="text-center mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Both attributed to</div>
        <div className="font-display text-2xl text-gold mt-1">{round.author}</div>
        <div className="text-sm text-muted-foreground mt-1">Which one did they actually say?</div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {round.options.map((opt, i) => {
          const isCorrect = picked !== null && i === correctIdx;
          const isWrong = picked === i && i !== correctIdx;
          const dim = picked !== null && i !== correctIdx && picked !== i;
          return (
            <button key={i} onClick={() => answer(i)} disabled={picked !== null}
              className={`relative rounded-2xl border p-6 sm:p-8 text-left transition shadow-elegant grain animate-reveal ${
                isCorrect ? "border-success bg-success/10" : isWrong ? "border-error bg-error/10 animate-shake" : dim ? "border-border opacity-50" : "border-border bg-card hover:border-gold"
              }`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg font-mono font-bold ${isCorrect ? "bg-success text-ink" : isWrong ? "bg-error text-cream" : "bg-secondary text-gold"}`}>{ANSWER_LETTERS[i]}</span>
                {picked !== null && (
                  <span className={`text-xs uppercase tracking-widest font-bold ${opt.real ? "text-success" : "text-error"}`}>
                    {opt.real ? "✓ Real" : "✗ Forged"}
                  </span>
                )}
              </div>
              <p className={`font-display leading-snug ${live ? "text-xl sm:text-2xl" : "text-lg"}`}>"{opt.text}"</p>
            </button>
          );
        })}
      </div>

      {picked !== null && (
        <div className="mt-6 flex items-center justify-between rounded-xl border border-border bg-card p-4">
          <div className="text-sm">
            {picked === correctIdx ? <span className="text-success font-medium">✓ A keen eye.</span> : <span className="text-error font-medium">✗ The fake got you this round.</span>}
          </div>
          <button onClick={next} className="rounded-full bg-gradient-gold px-5 py-2 text-sm font-semibold text-ink">{idx + 1 >= rounds.length ? "See results" : "Next →"}</button>
        </div>
      )}
    </GameShell>
  );
}
